import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { apiFetch } from "../../utils/api";
import { ValidationRun } from "./types";
import { API, errMsg, fmtInt, fmtDate, Header, ErrorBox, STATUS_COLORS, pageCls } from "./shared";

// Provider validation runs against our wallet, newest first; click to see cases.
export default function Validation() {
  const [runs, setRuns] = useState<ValidationRun[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await apiFetch<{ runs: ValidationRun[] }>(API + "/validation-runs");
      setRuns(d.runs || []);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className={pageCls}>
      <Header title="Original Games — Validation" subtitle="Integration validation runs against our wallet" onRefresh={load} loading={loading} />
      <ErrorBox error={error} />
      <div className="space-y-3">
        {runs.map((r) => {
          const cases = (r.response && r.response.cases) || [];
          const isOpen = open === r.run_id;
          return (
            <div key={r.run_id} className="bg-[#0E1831] rounded-xl border border-[#1E2D55] overflow-hidden">
              <button onClick={() => setOpen(isOpen ? null : r.run_id)} className="w-full p-4 flex flex-wrap gap-x-6 gap-y-1 items-center text-left hover:bg-[#162140]">
                <span className={`font-semibold ${STATUS_COLORS[r.status || ""] || ""}`}>{r.status || "—"}</span>
                <span className="font-mono">{fmtInt(r.cases_passed)}/{fmtInt(r.cases_total)} passed</span>
                <span className="text-[#878AA2]">{r.currency || "—"}{r.game_code ? ` · ${r.game_code}` : ""}</span>
                <span className="text-[#878AA2]">plan {r.plan_version || "—"}</span>
                <span className="text-[#878AA2] ml-auto">{fmtDate(r.created_at)}</span>
              </button>
              {isOpen && (
                <div className="border-t border-[#1E2D55] p-4 space-y-2">
                  <div className="text-xs text-[#878AA2] font-mono">run {r.run_id}</div>
                  {cases.map((c) => (
                    <div key={c.id} className="rounded-lg bg-[#0C0D1D] border border-[#1E2D55] p-3 text-sm">
                      <div className="flex items-center gap-2 flex-wrap">
                        {c.verdict === "pass" ? <CheckCircle2 size={16} className="text-[#0ECC68]" /> : <XCircle size={16} className="text-[#E01B4F]" />}
                        <span className="font-medium">{c.title || c.id}</span>
                        <span className="text-xs text-[#878AA2] font-mono">{c.id}</span>
                        {c.required && <span className="text-xs text-[#FFC23F]">required</span>}
                      </div>
                      {c.expects && <p className="text-xs text-[#878AA2] mt-1">Expects: {c.expects}</p>}
                      {c.detail !== undefined && (
                        <details className="mt-1">
                          <summary className="text-xs text-[#4A85F7] cursor-pointer">Detail</summary>
                          <pre className="mt-1 text-xs bg-[#070815] p-2 rounded overflow-auto">{JSON.stringify(c.detail, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  ))}
                  {cases.length === 0 && <pre className="text-xs bg-[#070815] p-2 rounded overflow-auto">{JSON.stringify(r.response, null, 2)}</pre>}
                </div>
              )}
            </div>
          );
        })}
        {!loading && !error && runs.length === 0 && <p className="text-[#878AA2]">No validation runs recorded.</p>}
      </div>
    </div>
  );
}
